import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Checkbox, Box, Button, TextField, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { SimpleCustomTableProps, RowId, SimpleColumn, SimpleRow } from './interface';
import { nowOperationalDateTimeString } from '../../utils/operationalDateTime';
import { shouldHideForbiddenActions } from '../../utils/permission-ui';

const TABLE_HEADER_BACKGROUND = '#FFAA77';

const normalizeColumnIdentifier = (value?: string | number | null) =>
  String(value ?? '').trim().toLowerCase();

const isActionLikeColumn = (column: SimpleColumn) => {
  const field = normalizeColumnIdentifier(column.field);
  const header = normalizeColumnIdentifier(column.headerName);
  const hasBlankHeader = header === '';
  const looksLikeOptionField =
    field.startsWith('opt_') ||
    field.startsWith('action_') ||
    field.endsWith('_action') ||
    field.endsWith('_actions');

  return (
    field === 'actions' ||
    field === 'options' ||
    field === 'opciones' ||
    header === 'opciones' ||
    header === 'acciones' ||
    looksLikeOptionField ||
    (hasBlankHeader && typeof column.renderCell === 'function')
  );
};

const hasRenderableNode = (node: React.ReactNode): boolean => {
  if (node === null || node === undefined || node === false || node === '') {
    return false;
  }

  if (Array.isArray(node)) {
    return node.some(hasRenderableNode);
  }

  if (React.isValidElement(node)) {
    const allowedProp = (node.props as { allowed?: unknown } | null)?.allowed;
    if (allowedProp === false && shouldHideForbiddenActions()) {
      return false;
    }

    if (node.type === React.Fragment) {
      return hasRenderableNode((node.props as { children?: React.ReactNode } | null)?.children);
    }

    return true;
  }

  return true;
};

const isSelectionDisabledRow = (row: SimpleRow) => Boolean((row as { disableSelection?: boolean } | null)?.disableSelection);

type InternalProps = SimpleCustomTableProps;

const SimpleCustomTable: React.FC<InternalProps> = ({
  rows,
  columns,
  enableSearch = false,
  searchPlaceholder = 'Buscar...',
  countValue,
  checkboxSelection = false,
  selectionModel = [],
  onRowSelectionModelChange,
  maxHeight = 400,
  disableInternalScroll = false,
  showBorders = true,
  showCount = true,
  exportCsv = true,
  exportFileName = 'export.csv',
  csvDelimiter = ';',
  wrapCellContent = false,
  autoRowHeight = false,
  columnGroups = [],
  rowHeight = 36,
  getRowCellSx,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selected, setSelected] = useState<RowId[]>(selectionModel);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setSelected(selectionModel);
  }, [selectionModel]);

  const disableVerticalScroll =
    disableInternalScroll === true ||
    disableInternalScroll === 'vertical' ||
    disableInternalScroll === 'all';
  const disableHorizontalScroll =
    disableInternalScroll === true ||
    disableInternalScroll === 'horizontal' ||
    disableInternalScroll === 'all';

  const visibleColumns = useMemo(() => {
    return columns.filter((column) => {
      if (!isActionLikeColumn(column)) {
        return true;
      }

      if (rows.length === 0) {
        return false;
      }

      return rows.some((row) => {
        if (column.renderCell) {
          return hasRenderableNode(column.renderCell({ row }));
        }

        return hasRenderableNode(row[column.field]);
      });
    });
  }, [columns, rows]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!enableSearch || normalizedSearch === '') {
      return rows;
    }

    const searchableColumns = visibleColumns.filter((column) => !isActionLikeColumn(column));

    return rows.filter((row) =>
      searchableColumns.some((column) => {
        const rawValue = row[column.field];
        if (rawValue === null || rawValue === undefined) {
          return false;
        }

        return String(rawValue).toLowerCase().includes(normalizedSearch);
      })
    );
  }, [enableSearch, rows, searchTerm, visibleColumns]);

  // sin paginación: mostramos TODO
  const pageRows = useMemo(() => filteredRows, [filteredRows]);
  const effectiveRowsForHeader = pageRows.filter((row) => !isSelectionDisabledRow(row));
  const displayedCount = countValue ?? pageRows.length;

  const handleApplySearch = () => {
    setSearchTerm(searchInputRef.current?.value ?? '');
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const addIds: RowId[] = effectiveRowsForHeader.map((r) => r.id);
      const setIds = new Set([...selected, ...addIds]);
      const newSelecteds = Array.from(setIds);
      setSelected(newSelecteds);
      onRowSelectionModelChange?.(newSelecteds);
      return;
    }
    const removeSet = new Set(effectiveRowsForHeader.map(r => r.id));
    const remaining = selected.filter(id => !removeSet.has(id));
    setSelected(remaining);
    onRowSelectionModelChange?.(remaining);
  };

  const handleSelectRow = (rowId: RowId) => {
    const exists = selected.includes(rowId);
    const newSelected = exists ? selected.filter(id => id !== rowId) : [...selected, rowId];
    setSelected(newSelected);
    onRowSelectionModelChange?.(newSelected);
  };

  const isSelected = (rowId: RowId) => selected.includes(rowId);

  const pageSelectedCount = effectiveRowsForHeader.reduce((acc, r) => acc + (selected.includes(r.id) ? 1 : 0), 0);
  const headerChecked = effectiveRowsForHeader.length > 0 && pageSelectedCount === effectiveRowsForHeader.length;
  const headerIndeterminate = pageSelectedCount > 0 && pageSelectedCount < effectiveRowsForHeader.length;

  // 🔹 Construcción CSV
  const escapeCsv = (val: string) => {
    if (!val) return '';
    const mustQuote = val.includes(csvDelimiter) || val.includes('"') || /\r|\n/.test(val);
    let s = val.replace(/"/g, '""');
    return mustQuote ? `"${s}"` : s;
  };

  const buildCsv = () => {
    const headers = visibleColumns.map(c => c.headerName ?? c.field);
    const headerLine = headers.map(escapeCsv).join(csvDelimiter);

    const bodyLines = rows.map(row => {
      const cells = visibleColumns.map(c => {
        const v = row[c.field];
        return escapeCsv(v != null ? String(v) : '');
      });
      return cells.join(csvDelimiter);
    });

    return '\uFEFF' + [headerLine, ...bodyLines].join('\n'); // BOM para Excel
  };

  const downloadCsv = () => {
    const csv = buildCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const timestamp = nowOperationalDateTimeString().replace(/[-: ]/g, '_');
    const fileBase = exportFileName.replace(/\.csv$/i, '');
    const fileNameWithDate = `${fileBase}_${timestamp}.csv`;

    link.href = url;
    link.setAttribute('download', fileNameWithDate);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
  };


  const tableStyles = useMemo(() => {
    const bodyCellHeightStyles = autoRowHeight
      ? {
        height: 'auto' as const,
        minHeight: `${rowHeight}px`,
        lineHeight: 'normal' as const,
        verticalAlign: 'top' as const,
      }
      : {
        height: `${rowHeight}px`,
        lineHeight: `${rowHeight - 5}px`,
      };

    const rowHeightStyles = autoRowHeight
      ? { height: 'auto' as const, minHeight: `${rowHeight}px` }
      : { height: `${rowHeight}px` };

    return {
      border: showBorders ? '1px solid #e0e0e0' : 'none',
      '& .MuiTable-root': {
        borderCollapse: 'separate',
        borderSpacing: 0,
        width: 'max-content',
        minWidth: '100%',
      },
      '& .MuiTableHead-root .MuiTableRow-root': { backgroundColor: TABLE_HEADER_BACKGROUND },
      '& .MuiTableCell-head': {
        borderRight: showBorders ? '1px solid #e0e0e0' : 'none',
        borderBottom: showBorders ? '2px solid #e0e0e0' : 'none',
        backgroundColor: TABLE_HEADER_BACKGROUND,
        color: '#212121',
        fontWeight: 650,
        fontSize: '0.8rem',
        padding: '2px 4px',
        height: `${rowHeight}px`,
        '&:last-child': { borderRight: 'none', paddingRight: '12px' },
      },
      '& .MuiTableCell-body': {
        borderRight: showBorders ? '1px solid #e0e0e0' : 'none',
        borderBottom: showBorders ? '1px solid #e0e0e0' : 'none',
        padding: '1px 5px',
        fontSize: '0.8rem',
        ...bodyCellHeightStyles,
        '&:last-child': { borderRight: 'none', paddingRight: '12px' },
      },
      '& .MuiTableRow-root': {
        ...rowHeightStyles,
        '&:last-child .MuiTableCell-body': { borderBottom: showBorders ? '1px solid #e0e0e0' : 'none' },
        '&:hover': { backgroundColor: '#f8f9fa' },
        '&.Mui-selected': {
          backgroundColor: '#e3f2fd',
          '&:hover': { backgroundColor: '#bbdefb' },
        },
      },
      '& .MuiCheckbox-root': { padding: '4px' },
    };
  }, [showBorders, rowHeight, autoRowHeight]);

  const columnGroupMeta = useMemo(() => {
    if (!columnGroups || columnGroups.length === 0) {
      return {
        hasGroups: false,
        fieldToGroup: new Map<string, string>(),
        groupToCount: new Map<string, number>(),
      };
    }

    const fieldToGroup = new Map<string, string>();
    const groupToCount = new Map<string, number>();
    const columnFieldSet = new Set(columns.map(col => col.field));

    columnGroups.forEach((group) => {
      let count = 0;
      group.fields.forEach((field) => {
        if (columnFieldSet.has(field)) {
          fieldToGroup.set(field, group.header);
          count += 1;
        }
      });
      if (count > 0) {
        groupToCount.set(group.header, count);
      }
    });

    return {
      hasGroups: groupToCount.size > 0,
      fieldToGroup,
      groupToCount,
    };
  }, [visibleColumns, columnGroups]);

  const buildCellSx = (column: SimpleColumn, shouldWrap: boolean) => ({
    width: column.width || 'auto',
    minWidth: column.minWidth || (column.width ? column.width : '80px'),
    maxWidth: column.maxWidth || 'none',
    whiteSpace: shouldWrap ? 'normal' : 'nowrap',
    overflow: 'hidden',
    textOverflow: shouldWrap ? 'unset' : 'ellipsis',
    ...(shouldWrap ? { wordBreak: 'break-word', overflowWrap: 'anywhere' } : {}),
  });

  const mergeHeaderCellSx = (column: SimpleColumn, shouldWrap: boolean): SxProps<Theme> =>
    [buildCellSx(column, shouldWrap), column.headerCellSx] as SxProps<Theme>;

  const mergeBodyCellSx = (column: SimpleColumn, shouldWrap: boolean, row: SimpleRow): SxProps<Theme> =>
    [
      buildCellSx(column, shouldWrap),
      getRowCellSx?.({ row }),
      column.getCellSx?.({ row }),
    ] as SxProps<Theme>;

  const selectionCellSx = { width: '60px', minWidth: '60px', maxWidth: '60px' } as const;
  const headerGroupCellSx = {
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '12px 8px',
    backgroundColor: TABLE_HEADER_BACKGROUND,
    color: '#212121',
  };

  const { hasGroups, fieldToGroup, groupToCount } = columnGroupMeta;

  const shouldRenderGroupSubHeaders = useMemo(() => {
    if (!hasGroups) {
      return false;
    }

    return visibleColumns.some((column) => {
      if (!fieldToGroup.has(column.field)) {
        return false;
      }
      const label = column.headerName !== undefined ? column.headerName : column.field;
      return (label ?? '').trim() !== '';
    });
  }, [hasGroups, visibleColumns, fieldToGroup]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* 🔹 Barra superior con contador y botón export */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="nowrap" gap={0.5} mb={1}>
        <Box display="flex" alignItems="center" gap={0.5} flexWrap="nowrap" sx={{ minWidth: 0, flex: 1 }}>
          {showCount && (
            <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {displayedCount.toLocaleString()} {displayedCount === 1 ? 'registro' : 'registros'}
            </Box>
          )}
          {enableSearch && (
            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                handleApplySearch();
              }}
              sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', alignItems: 'stretch', minWidth: 0, flex: 1 }}
            >
              <TextField
                size="small"
                onChange={(event) => {
                  const nextValue = event.target.value;
                  if (nextValue.trim() === '') {
                    setSearchTerm('');
                  }
                }}
                inputRef={searchInputRef}
                placeholder={searchPlaceholder}
                sx={{ minWidth: 0, flex: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                type="submit"
                startIcon={isSmallScreen ? undefined : <SearchIcon />}
                aria-label="Buscar"
                sx={isSmallScreen ? { minWidth: 40, width: 40, height: 40, p: 0 } : { minHeight: 40 }}
              >
                {isSmallScreen ? <SearchIcon /> : 'Buscar'}
              </Button>
            </Box>
          )}
        </Box>
        {exportCsv && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={downloadCsv}
            aria-label="Exportar CSV"
            sx={isSmallScreen ? { minWidth: 40, width: 40, height: 40, p: 0, flexShrink: 0 } : { minHeight: 40, flexShrink: 0 }}
          >
            {isSmallScreen ? null : 'Exportar CSV'}
          </Button>
        )}
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          ...(!disableVerticalScroll ? { maxHeight } : {}),
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: disableHorizontalScroll ? 'visible' : 'auto',
          overflowY: disableVerticalScroll ? 'visible' : 'auto',
          scrollbarColor: '#01274d #d9e1ea',
          '&::-webkit-scrollbar': {
            width: 14,
            height: 14,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#d9e1ea',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#01274d',
            borderRadius: 999,
            border: '3px solid #d9e1ea',
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: '#d9e1ea',
          },
          ...tableStyles,
        }}
        elevation={showBorders ? 1 : 0}
      >
        <Table stickyHeader size="small" aria-label="simple custom table">
          <TableHead>
            {hasGroups ? (
              <>
                <TableRow>
                  {checkboxSelection && (
                    <TableCell
                      padding="checkbox"
                      sx={selectionCellSx}
                      rowSpan={shouldRenderGroupSubHeaders ? 2 : 1}
                    >
                      <Checkbox
                        indeterminate={headerIndeterminate}
                        checked={headerChecked}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  {(() => {
                    const headerCells: React.ReactNode[] = [];
                    const processedGroups = new Set<string>();

                    visibleColumns.forEach((column) => {
                      const shouldWrap = column.wrapText ?? wrapCellContent;
                      const groupHeader = fieldToGroup.get(column.field);
                      const headerContent = column.renderHeader ? column.renderHeader() : (column.headerName ?? column.field);

                      if (groupHeader) {
                        if (!processedGroups.has(groupHeader)) {
                          const colSpan = groupToCount.get(groupHeader) ?? 0;
                          if (colSpan > 0) {
                            headerCells.push(
                              <TableCell
                                key={`group-${groupHeader}`}
                                align="center"
                                colSpan={colSpan}
                                sx={headerGroupCellSx}
                              >
                                {groupHeader}
                              </TableCell>
                            );
                          }
                          processedGroups.add(groupHeader);
                        }
                      } else {
                        headerCells.push(
                          <TableCell
                            key={column.field}
                            align={column.align || 'left'}
                            rowSpan={shouldRenderGroupSubHeaders ? 2 : 1}
                            sx={mergeHeaderCellSx(column, shouldWrap)}
                          >
                            {headerContent}
                          </TableCell>
                        );
                      }
                    });

                    return headerCells;
                  })()}
                </TableRow>
                {shouldRenderGroupSubHeaders && (
                  <TableRow>
                    {visibleColumns.map((column) => {
                      const groupHeader = fieldToGroup.get(column.field);
                      if (!groupHeader) {
                        return null;
                      }
                      const shouldWrap = column.wrapText ?? wrapCellContent;
                      const headerContent = column.renderHeader ? column.renderHeader() : (column.headerName ?? column.field);
                      return (
                        <TableCell
                          key={column.field}
                          align={column.align || 'left'}
                          sx={mergeHeaderCellSx(column, shouldWrap)}
                        >
                          {headerContent}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}
              </>
            ) : (
              <TableRow>
                {checkboxSelection && (
                  <TableCell padding="checkbox" sx={selectionCellSx}>
                    <Checkbox
                      indeterminate={headerIndeterminate}
                      checked={headerChecked}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                {visibleColumns.map((column) => {
                  const shouldWrap = column.wrapText ?? wrapCellContent;
                  const headerContent = column.renderHeader ? column.renderHeader() : (column.headerName ?? column.field);
                  return (
                    <TableCell
                      key={column.field}
                      align={column.align || 'left'}
                      sx={mergeHeaderCellSx(column, shouldWrap)}
                    >
                      {headerContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {pageRows.map((row) => {
              const isItemSelected = isSelected(row.id);
              return (
                <TableRow
                  key={row.id}
                  hover
                  selected={isItemSelected}
                  role="checkbox"
                  aria-checked={isItemSelected}
                >
                  {checkboxSelection && (
                    <TableCell padding="checkbox" sx={selectionCellSx}>
                      {isSelectionDisabledRow(row as SimpleRow) ? null : (
                        <Checkbox
                          checked={isItemSelected}
                          onChange={() => handleSelectRow(row.id)}
                        />
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.map((column) => {
                    const shouldWrap = column.wrapText ?? wrapCellContent;
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        sx={mergeBodyCellSx(column, shouldWrap, row as SimpleRow)}
                      >
                        {column.renderCell ? column.renderCell({ row }) : row[column.field]}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SimpleCustomTable;
